import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { albumService } from '../../services/api';
import { useAuth } from '../../hooks/useAuth';
import { stickerCode } from '../albumData';
import { useAlbumTrades } from '../hooks/useAlbumTrades';
import { buildWhatsappLink, buildMailtoLink, buildTradeMessage } from '../utils/whatsapp';
import OffersBrowserSection from './OffersBrowserSection';

/**
 * Album2Dashboard
 *
 * Dashboard embebido exclusivo de /album2. Vive dentro del panel derecho
 * expandible de StickerAlbum2. Es independiente de /album/dashboard
 * (AlbumDashboard.js) para que cambios visuales acá no afecten la página
 * full-screen del compañero.
 *
 * Decisiones de diseño:
 * - Una única UnifiedRow para Activas/Recibidas/Enviadas/Mis ofertas/Historial.
 *   Mismo avatar (44px), mismo StatusChip, mismas posiciones de las acciones.
 * - Para ofertas, el avatar es un círculo con swap_horiz del mismo tamaño.
 * - Chips sin labels DAS/PEDÍS — los chips dados van rojos, los recibidos verdes.
 * - Tamaño de chip se adapta al total de la fila (chico si hay muchos, grande
 *   si pocos), de forma que naturalmente puedan llenar dos filas.
 * - Botones de acción `text-sm` y padding `px-3 py-2`.
 * - Todas las acciones de cancelación dicen simplemente "Cancelar".
 */

const ALL_TABS = ['Todos', 'Activas', 'Recibidas', 'Enviadas', 'Historial', 'Explorar', 'Mis ofertas'];
const OPEN_OFFER_TABS = new Set(['Explorar', 'Mis ofertas']);

export const DASHBOARD_VARIANT_NAMES = ['Con ofertas abiertas', 'Sin ofertas abiertas'];

const STATUS_CHIP = {
  ACCEPTED:  { label: 'Reservado', cls: 'bg-primary/10 text-primary' },
  PENDING:   { label: 'Pendiente', cls: 'bg-amber-100 text-amber-800' },
  COMPLETED: { label: 'Completado', cls: 'bg-surface-container-highest text-on-surface-variant' },
  CANCELLED: { label: 'Cancelado', cls: 'bg-red-50 text-red-700' },
  REJECTED:  { label: 'Rechazado', cls: 'bg-red-50 text-red-700' },
  OPEN:      { label: 'Abierta', cls: 'bg-amber-100 text-amber-800' },
  CLOSED:    { label: 'Cerrada', cls: 'bg-surface-container-highest text-on-surface-variant' },
};

function timeAgo(dateValue) {
  if (!dateValue) return '';
  const d = Array.isArray(dateValue)
    ? new Date(dateValue[0], dateValue[1] - 1, dateValue[2], dateValue[3] || 0, dateValue[4] || 0, dateValue[5] || 0)
    : new Date(dateValue);
  if (!d || isNaN(d.getTime())) return '';
  const diffMin = Math.floor((Date.now() - d.getTime()) / 60000);
  if (diffMin < 1) return 'justo ahora';
  if (diffMin < 60) return `${diffMin}m`;
  const diffH = Math.floor(diffMin / 60);
  if (diffH < 24) return `${diffH}h`;
  const diffD = Math.floor(diffH / 24);
  if (diffD < 7) return `${diffD}d`;
  return `${Math.floor(diffD / 7)}sem`;
}

function StatusChip({ status }) {
  const cfg = STATUS_CHIP[status] || STATUS_CHIP.PENDING;
  return (
    <span className={`inline-flex items-center px-2.5 py-1 rounded-full text-xs font-bold uppercase tracking-wide whitespace-nowrap ${cfg.cls}`}>
      {cfg.label}
    </span>
  );
}

function chipSizeFor(total) {
  if (total <= 6)  return 'px-2 py-0.5 text-[12px]';
  if (total <= 12) return 'px-1.5 py-0.5 text-[11px]';
  return 'px-1 py-0.5 text-[10px]';
}

function StickerChipsLine({ giveNums, getNums }) {
  const give = giveNums || [];
  const get  = getNums || [];
  if (give.length === 0 && get.length === 0) return null;
  const total = give.length + get.length;
  const sz = chipSizeFor(total);
  const MAX_PER_SIDE = 5;
  const giveVisible = give.slice(0, MAX_PER_SIDE);
  const giveOverflow = give.length - giveVisible.length;
  const getVisible = get.slice(0, MAX_PER_SIDE);
  const getOverflow = get.length - getVisible.length;
  return (
    <div className="flex flex-wrap items-center gap-1">
      {giveVisible.map(n => (
        <span key={`g${n}`} className={`rounded font-bold tabular-nums ${sz}`} style={{ backgroundColor: '#ef444422', color: '#b91c1c' }}>
          {stickerCode(n)}
        </span>
      ))}
      {giveOverflow > 0 && (
        <span className={`rounded font-bold tabular-nums ${sz}`} style={{ backgroundColor: '#ef444422', color: '#b91c1c' }}>
          +{giveOverflow}
        </span>
      )}
      {getVisible.map(n => (
        <span key={`r${n}`} className={`rounded font-bold tabular-nums ${sz}`} style={{ backgroundColor: '#16a34a22', color: '#15803d' }}>
          {stickerCode(n)}
        </span>
      ))}
      {getOverflow > 0 && (
        <span className={`rounded font-bold tabular-nums ${sz}`} style={{ backgroundColor: '#16a34a22', color: '#15803d' }}>
          +{getOverflow}
        </span>
      )}
    </div>
  );
}

/* Avatar circular de 44px. Acepta `user` (con color/initial) o `icon`. */
function RowAvatar({ user, icon }) {
  if (icon) {
    return (
      <div className="w-11 h-11 rounded-full bg-primary/10 flex items-center justify-center shrink-0">
        <span className="material-symbols-outlined text-xl text-primary">{icon}</span>
      </div>
    );
  }
  return (
    <div
      className="w-11 h-11 rounded-full flex items-center justify-center text-white text-sm font-bold shrink-0"
      style={{ backgroundColor: user?.color || '#6b7280' }}
    >
      {user?.initial || '?'}
    </div>
  );
}

function UnifiedRow({ avatar, icon, title, subtitle, giveNums, getNums, status, actions, onClick, tint, showChipsInline = false, hideStatus = false }) {
  const tintBg     = tint?.bg     ?? 'bg-surface-container/50';
  const tintBorder = tint?.border ?? 'border-l-outline-variant';
  const tintHover  = tint?.hover  ?? 'hover:bg-surface-container';
  const give = giveNums || [];
  const get  = getNums || [];
  const hasChips = give.length > 0 || get.length > 0;
  return (
    <div
      className={`relative flex items-center gap-4 pl-3 pr-4 py-4 ${tintBg} ${tintHover} border-l-4 ${tintBorder} transition-colors group cursor-pointer border-b border-outline-variant/10 last:border-b-0`}
      onClick={onClick}
    >
      <RowAvatar user={avatar} icon={icon} />
      <div className="flex-1 min-w-0">
        <p className="text-base font-bold text-on-surface truncate">{title}</p>
        {subtitle && <p className="text-xs text-on-surface-variant mt-0.5">{subtitle}</p>}
      </div>
      {showChipsInline && hasChips && (
        <StickerChipsLine giveNums={give} getNums={get} />
      )}
      {!hideStatus && <StatusChip status={status} />}
      {actions && (
        <div className="flex items-center gap-2 shrink-0" onClick={e => e.stopPropagation()}>
          {actions}
        </div>
      )}
      {!showChipsInline && hasChips && (
        <div className="hidden group-hover:block absolute left-4 right-4 top-full mt-1 z-50 bg-surface ring-1 ring-outline-variant rounded-xl shadow-2xl p-3">
          <StickerChipsLine giveNums={give} getNums={get} />
        </div>
      )}
    </div>
  );
}

const tintFor = (trade) => {
  if (trade.status === 'ACCEPTED') return { bg: 'bg-primary/8',  border: 'border-l-primary',     hover: 'hover:bg-primary/12' };
  if (trade.direction === 'receiver' && trade.status === 'PENDING') return { bg: 'bg-amber-50', border: 'border-l-amber-500', hover: 'hover:bg-amber-100' };
  return { bg: 'bg-surface-container/50', border: 'border-l-outline-variant', hover: 'hover:bg-surface-container' };
};

/* Botón base usado por todas las acciones; mismo tamaño en cualquier fila.
   Por defecto solo ícono — el dashboard vive en un panel angosto, los labels
   no entran sin comerse el nombre del usuario. */
function ActionButton({ icon, label, onClick, variant = 'primary', title }) {
  const cls =
    variant === 'danger'
      ? 'border border-red-300 text-red-600 bg-transparent hover:bg-red-50'
      : variant === 'ghost'
        ? 'border border-outline bg-surface hover:bg-surface-container'
        : 'text-white hover:opacity-90';
  const style = variant === 'primary' ? { backgroundColor: 'var(--md-sys-color-primary, #2563eb)' } : undefined;
  return (
    <button
      type="button"
      onClick={onClick}
      title={title || label}
      aria-label={title || label}
      className={`flex items-center justify-center w-9 h-9 rounded-lg transition-colors shrink-0 ${cls}`}
      style={style}
    >
      {icon && <span className="material-symbols-outlined text-base" style={variant === 'ghost' && icon === 'chat' ? { color: '#25D366' } : undefined}>{icon}</span>}
    </button>
  );
}

/* ── helpers ───────────────────────────────────────────────────────── */

function EmptyState({ icon, title, linkText, onLink }) {
  return (
    <div className="py-16 flex flex-col items-center justify-center text-center px-4">
      <span className="material-symbols-outlined text-5xl text-outline-variant mb-4">{icon}</span>
      <p className="text-sm font-bold text-on-surface mb-1">{title}</p>
      {linkText && onLink && (
        <button
          type="button"
          onClick={onLink}
          className="mt-2 text-sm font-bold text-primary cursor-pointer hover:opacity-80 transition-opacity flex items-center gap-1"
        >
          {linkText}
          <span className="material-symbols-outlined text-sm">arrow_forward</span>
        </button>
      )}
    </div>
  );
}

function LoadingRows({ count = 4 }) {
  return (
    <div className="divide-y divide-outline-variant/10">
      {Array.from({ length: count }, (_, i) => (
        <div key={i} className="px-4 py-4 animate-pulse flex items-center gap-3">
          <div className="w-11 h-11 rounded-full bg-surface-container-highest shrink-0" />
          <div className="flex-1 space-y-2">
            <div className="h-3 bg-surface-container-highest rounded w-2/5" />
            <div className="h-2 bg-surface-container-highest rounded w-1/3" />
          </div>
          <div className="h-6 bg-surface-container-highest rounded-full w-20" />
        </div>
      ))}
    </div>
  );
}

function SectionHeader({ icon, label, count }) {
  return (
    <div className="flex items-center gap-2 px-4 py-2 bg-surface-container border-b border-outline-variant/20">
      <span className="material-symbols-outlined text-sm text-on-surface-variant">{icon}</span>
      <span className="text-[11px] font-black uppercase tracking-widest text-on-surface-variant">{label}</span>
      <span className="ml-auto text-[10px] font-bold px-2 py-0.5 rounded-full bg-primary/10 text-primary tabular-nums">
        {count}
      </span>
    </div>
  );
}

/* ── MiniMetric (chip teñido + popover) ────────────────────────────── */

function MiniMetric({ bucket, label, value, trades, onPick }) {
  const palette = {
    activas:     { num: 'text-primary',     bg: 'bg-primary/10',         border: 'border-primary/40',     dot: 'bg-primary' },
    recibidas:   { num: 'text-amber-700',   bg: 'bg-amber-100',          border: 'border-amber-400',      dot: 'bg-amber-500' },
    enviadas:    { num: 'text-on-surface',  bg: 'bg-surface-container',  border: 'border-outline-variant',dot: 'bg-on-surface-variant' },
    completadas: { num: 'text-on-surface-variant', bg: 'bg-surface-container-highest', border: 'border-outline-variant', dot: 'bg-outline-variant' },
  }[bucket] || { num: 'text-on-surface', bg: 'bg-surface-container', border: 'border-outline-variant', dot: 'bg-on-surface-variant' };

  const hasTrades = trades && trades.length > 0;

  return (
    <div className={`relative group ${hasTrades ? 'cursor-default' : ''}`}>
      <div className={`flex flex-col items-center justify-center px-2 py-2 rounded-xl border ${palette.bg} ${palette.border}`}>
        <div className="flex items-center gap-1.5">
          <span className={`w-1.5 h-1.5 rounded-full ${palette.dot}`} aria-hidden="true" />
          <span className={`text-lg font-black tabular-nums ${palette.num}`}>{value}</span>
        </div>
        <span className="text-[10px] font-bold uppercase tracking-wider text-on-surface-variant mt-0.5">{label}</span>
      </div>
      {hasTrades && (
        <div className="hidden group-hover:block absolute left-0 right-0 top-full mt-1 z-30 bg-surface border border-outline rounded-xl shadow-2xl p-2 max-h-72 overflow-y-auto pointer-events-auto">
          {trades.slice(0, 6).map(tr => (
            <button
              type="button"
              key={tr.id}
              onClick={() => onPick?.(tr)}
              className="w-full flex items-center gap-2 px-2 py-1.5 rounded-lg hover:bg-surface-container text-left"
            >
              <div className="w-7 h-7 rounded-full flex items-center justify-center text-white text-[10px] font-bold shrink-0" style={{ backgroundColor: tr.otherUser?.color || '#6b7280' }}>
                {tr.otherUser?.initial || '?'}
              </div>
              <span className="flex-1 text-xs font-bold text-on-surface truncate">{tr.otherUser?.name}</span>
              <span className="text-[10px] text-on-surface-variant tabular-nums">
                {(tr.youGive?.length || 0)}↧ {(tr.youReceive?.length || 0)}↥
              </span>
            </button>
          ))}
          {trades.length > 6 && (
            <p className="px-2 py-1 text-[10px] text-on-surface-variant text-center">+{trades.length - 6} {label.toLowerCase()}</p>
          )}
        </div>
      )}
    </div>
  );
}

/* ── Main component ────────────────────────────────────────────────── */

function Album2Dashboard({ onClose, onCreateOffer, myCommunities, myUserId: myUserIdProp, dashboardVariant = 0, dispatch: albumDispatch }) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { user } = useAuth();
  const myUserId = myUserIdProp ?? user?.id ?? null;

  const tradesHook = useAlbumTrades();

  /* ── Computed tab list based on dashboardVariant ── */
  const hideOpenOffers = dashboardVariant === 1;
  const TABS = hideOpenOffers
    ? ALL_TABS.filter(tab => !OPEN_OFFER_TABS.has(tab))
    : ALL_TABS;

  /* ── My offers (includes CLOSED; split at render time) ── */
  const [myOffers, setMyOffers] = useState([]);
  const [offersLoading, setOffersLoading] = useState(false);
  const loadMyOffers = useCallback(async () => {
    setOffersLoading(true);
    try {
      const res = await albumService.listOffers('mine');
      const raw = Array.isArray(res.data) ? res.data : (res.data?.data || []);
      // Keep all non-CANCELLED. CLOSED ones go to Historial; OPEN to Mis ofertas.
      setMyOffers(raw.filter(o => o.status !== 'CANCELLED'));
    } catch { /* silent */ } finally {
      setOffersLoading(false);
    }
  }, []);

  /* ── Tab state ── */
  const [activeTab, setActiveTab] = useState(() => {
    try {
      const tabParam = new URLSearchParams(window.location.search).get('tab');
      if (tabParam && ALL_TABS.includes(tabParam)) return tabParam;
    } catch {}
    return ALL_TABS[0];
  });

  // Fallback: si el tab activo queda oculto por un cambio de variante, volver a "Todos".
  useEffect(() => {
    const visibleTabs = dashboardVariant === 1
      ? ALL_TABS.filter(t => !OPEN_OFFER_TABS.has(t))
      : ALL_TABS;
    if (!visibleTabs.includes(activeTab)) {
      setActiveTab(visibleTabs[0]);
    }
  }, [dashboardVariant, activeTab]);

  useEffect(() => {
    if (activeTab === 'Mis ofertas' || activeTab === 'Historial' || activeTab === 'Todos') loadMyOffers();
  }, [activeTab, loadMyOffers]);

  /* ── Toast ── */
  const [toast, setToast] = useState(null);
  const showToast = useCallback((message, type = 'success') => {
    setToast({ message, type });
    setTimeout(() => setToast(null), 3500);
  }, []);

  /* ── Open trade in album ── */
  const openTrade = useCallback((trade) => {
    navigate(`/album?tradeId=${trade.id}`);
  }, [navigate]);
  const openOffer = useCallback((offerId) => {
    navigate(`/album?offerId=${offerId}`);
  }, [navigate]);

  // Revisar oferta propia: pinta las cartas en el álbum SIN navegar (solo dispatch).
  const reviewMyOffer = useCallback((offer) => {
    if (albumDispatch) {
      albumDispatch({ type: 'VIEW_OFFER_REVIEW', offer });
    } else {
      // Fallback: abrir via URL param.
      navigate(`/album?offerId=${offer.id}`);
    }
  }, [albumDispatch, navigate]);

  /* ── Action handlers ── */
  const handleAccept = useCallback(async (trade, e) => {
    e?.stopPropagation();
    try {
      await albumService.acceptTrade(trade.id);
      await tradesHook.refresh();
      showToast(t('Aceptado'));
    } catch { showToast(t('Error al aceptar la reserva'), 'error'); }
  }, [tradesHook, showToast, t]);

  const handleReject = useCallback(async (trade, e) => {
    e?.stopPropagation();
    try {
      await albumService.rejectTrade(trade.id);
      await tradesHook.refresh();
      showToast(t('Rechazado'));
    } catch { showToast(t('Error al rechazar la reserva'), 'error'); }
  }, [tradesHook, showToast, t]);

  const handleCancel = useCallback(async (trade, e) => {
    e?.stopPropagation();
    try {
      await albumService.cancelTrade(trade.id);
      await tradesHook.refresh();
      showToast(t('Cancelado'));
    } catch { showToast(t('No se pudo cancelar'), 'error'); }
  }, [tradesHook, showToast, t]);

  const myCompletesRef = useRef(new Set());
  const handleComplete = useCallback(async (trade, e) => {
    e?.stopPropagation();
    if (!window.confirm(t('¿Ya intercambiaron las figuritas en persona? Esto las moverá entre los álbumes y no se puede deshacer.'))) return;
    try {
      myCompletesRef.current.add(trade.id);
      await albumService.completeTrade(trade.id);
      await tradesHook.refresh();
      showToast(t('Intercambio completado'));
    } catch { showToast(t('No se pudo completar el intercambio'), 'error'); }
  }, [tradesHook, showToast, t]);

  const handleWhatsapp = useCallback(async (trade, e) => {
    e?.stopPropagation();
    const contact = await tradesHook.fetchContact(trade.id);
    const phone = contact?.phone;
    const message = buildTradeMessage(t, {
      theirName: trade.otherUser?.name,
      myName: user?.name,
      giveNums: trade.youGive,
      getNums: trade.youReceive,
    });
    const link = phone ? buildWhatsappLink(phone, message) : null;
    if (link) {
      window.open(link, '_blank', 'noopener,noreferrer');
      return;
    }
    // Sin phone no caemos a mailto — eso abría el odioso "elija una app
    // para abrir mailto:". Mejor un toast claro.
    showToast(t('El otro usuario no tiene número de WhatsApp asociado'), 'error');
  }, [tradesHook, showToast, t, user?.name]);

  const handleCancelOffer = useCallback(async (offerId, e) => {
    e?.stopPropagation();
    if (!window.confirm(t('¿Cancelar esta oferta? Los reclamos pendientes se rechazarán.'))) return;
    try {
      await albumService.cancelOffer(offerId);
      await loadMyOffers();
      showToast(t('Oferta cancelada'));
    } catch { showToast(t('No se pudo cancelar la oferta'), 'error'); }
  }, [loadMyOffers, showToast, t]);

  /* ── Metrics ── */
  const metrics = useMemo(() => ({
    activas: tradesHook.activeTrades.length,
    recibidas: tradesHook.incomingTrades.length,
    enviadas: tradesHook.outgoingTrades.length,
    completadas: tradesHook.historyTrades.length,
  }), [tradesHook.activeTrades, tradesHook.incomingTrades, tradesHook.outgoingTrades, tradesHook.historyTrades]);

  /* ── Row renderers ── */

  const renderTrade = (trade, kind) => {
    const subtitle = (
      <span className="flex items-center gap-2 tabular-nums whitespace-nowrap">
        <span style={{ color: '#b91c1c' }} className="font-bold">{(trade.youGive?.length || 0)} ↦</span>
        <span style={{ color: '#15803d' }} className="font-bold">↤ {(trade.youReceive?.length || 0)}</span>
        <span>· {timeAgo(trade.createdAt)}</span>
      </span>
    );
    const actions =
      kind === 'active' ? (
        <>
          <ActionButton icon="chat" label={t('Mensaje')} variant="ghost" onClick={(e) => handleWhatsapp(trade, e)} title={t('Abrir WhatsApp')} />
          <ActionButton icon="close" label={t('Cancelar')} variant="danger" onClick={(e) => handleCancel(trade, e)} />
          <ActionButton icon="check_circle" label={t('Completar')} variant="primary" onClick={(e) => handleComplete(trade, e)} />
        </>
      ) : kind === 'incoming' ? (
        <>
          <ActionButton icon="check" label={t('Aceptar')} variant="primary" onClick={(e) => handleAccept(trade, e)} />
          <ActionButton icon="close" label={t('Rechazar')} variant="danger" onClick={(e) => handleReject(trade, e)} />
        </>
      ) : kind === 'outgoing' ? (
        <ActionButton icon="close" label={t('Cancelar')} variant="danger" onClick={(e) => handleCancel(trade, e)} />
      ) : null;

    return (
      <UnifiedRow
        key={trade.id}
        avatar={trade.otherUser}
        title={trade.otherUser?.name}
        subtitle={subtitle}
        giveNums={trade.youGive}
        getNums={trade.youReceive}
        status={trade.status}
        hideStatus={kind === 'active'}
        actions={actions}
        onClick={() => openTrade(trade)}
        tint={tintFor(trade)}
      />
    );
  };

  const renderHistory = (trade) => (
    <UnifiedRow
      key={trade.id}
      avatar={trade.otherUser}
      title={trade.otherUser?.name}
      subtitle={timeAgo(trade.completedAt || trade.resolvedAt || trade.createdAt)}
      giveNums={trade.youGive}
      getNums={trade.youReceive}
      status={trade.status}
      onClick={() => openTrade(trade)}
    />
  );

  const renderOffer = (offer, { isMine = false } = {}) => {
    const isOpen = offer.status === 'OPEN';
    return (
      <UnifiedRow
        key={offer.id}
        icon="campaign"
        title={t('Oferta pública')}
        subtitle={`#${offer.id}`}
        giveNums={offer.offered}
        getNums={offer.requested}
        status={isOpen ? 'OPEN' : 'CLOSED'}
        showChipsInline
        actions={isMine && isOpen ? (
          <ActionButton icon="close" label={t('Cancelar')} variant="danger" onClick={(e) => handleCancelOffer(offer.id, e)} />
        ) : null}
        onClick={() => isMine ? reviewMyOffer(offer) : openOffer(offer.id)}
      />
    );
  };

  /* ── Derived offer lists ── */
  const openOffers   = useMemo(() => myOffers.filter(o => o.status !== 'CLOSED'), [myOffers]);
  const closedOffers = useMemo(() => myOffers.filter(o => o.status === 'CLOSED'), [myOffers]);

  /* ── Historial: solo trades completados (las ofertas cerradas no aparecen) ── */
  const historialItems = useMemo(
    () => tradesHook.historyTrades.map(tr => ({ _kind: 'trade', _date: tr.completedAt || tr.resolvedAt || tr.createdAt, ...tr })),
    [tradesHook.historyTrades]
  );

  /* ── Tab body ── */

  function TabBody() {
    const isTodos = activeTab === 'Todos';
    if (tradesHook.loading && activeTab !== 'Mis ofertas' && activeTab !== 'Explorar') return <LoadingRows />;
    if (offersLoading && (activeTab === 'Mis ofertas' || activeTab === 'Historial' || (isTodos && myOffers.length === 0))) return <LoadingRows />;

    if (activeTab === 'Explorar') {
      return (
        <div>
          {/* Botón crear oferta */}
          {onCreateOffer && (
            <div className="px-3 pt-3 pb-2">
              <button
                type="button"
                onClick={onCreateOffer}
                className="w-full flex items-center justify-center gap-2 px-4 py-2.5 rounded-xl text-sm font-bold text-white transition-all hover:opacity-90 active:scale-95"
                style={{ backgroundColor: 'var(--md-sys-color-primary, #2563eb)' }}
              >
                <span className="material-symbols-outlined text-base">campaign</span>
                {t('Publicar mi oferta')}
              </button>
            </div>
          )}
          <div className="px-0">
            <OffersBrowserSection
              communities={myCommunities || []}
              currentUserId={myUserId}
              onOpenOffer={(offer) => openOffer(offer.id)}
              embedded
            />
          </div>
        </div>
      );
    }

    if (isTodos) {
      const sections = [
        { key: 'Activas',   icon: 'swap_horiz',  label: t('Activas'),    list: tradesHook.activeTrades,   render: (tr) => renderTrade(tr, 'active') },
        { key: 'Recibidas', icon: 'inbox',       label: t('Recibidas'),  list: tradesHook.incomingTrades, render: (tr) => renderTrade(tr, 'incoming') },
        { key: 'Enviadas',  icon: 'outbox',      label: t('Enviadas'),   list: tradesHook.outgoingTrades, render: (tr) => renderTrade(tr, 'outgoing') },
        { key: 'Historial', icon: 'history',     label: t('Historial'),  list: historialItems,            render: (item) => {
            if (item._kind === 'offer') return React.cloneElement(renderOffer(item, { isMine: true }), { key: `offer-${item.id}` });
            return React.cloneElement(renderHistory(item), { key: `trade-${item.id}` });
          } },
        ...(hideOpenOffers ? [] : [{ key: 'Ofertas', icon: 'local_offer', label: t('Mis ofertas'), list: openOffers, render: (o) => renderOffer(o, { isMine: true }) }]),
      ];
      const hasAny = sections.some(s => s.list.length > 0);
      if (!hasAny) {
        return <EmptyState icon="swap_horiz" title={t('Todavía no tenés actividad en el álbum.')} linkText={t('Ir al álbum')} onLink={() => navigate('/album')} />;
      }
      return (
        <div>
          {sections.filter(s => s.list.length > 0).map(s => (
            <React.Fragment key={s.key}>
              <SectionHeader icon={s.icon} label={s.label} count={s.list.length} />
              {s.list.map(s.render)}
            </React.Fragment>
          ))}
        </div>
      );
    }

    if (activeTab === 'Activas') {
      const list = tradesHook.activeTrades;
      if (list.length === 0) return <EmptyState icon="swap_horiz" title={t('No tenés intercambios activos.')} linkText={t('Ir al álbum')} onLink={() => navigate('/album')} />;
      return <div>{list.map(tr => renderTrade(tr, 'active'))}</div>;
    }
    if (activeTab === 'Recibidas') {
      const list = tradesHook.incomingTrades;
      if (list.length === 0) return <EmptyState icon="inbox" title={t('No tenés propuestas recibidas.')} linkText={t('Ir al álbum')} onLink={() => navigate('/album')} />;
      return <div>{list.map(tr => renderTrade(tr, 'incoming'))}</div>;
    }
    if (activeTab === 'Enviadas') {
      const list = tradesHook.outgoingTrades;
      if (list.length === 0) return <EmptyState icon="outbox" title={t('No tenés propuestas enviadas.')} linkText={t('Ir al álbum')} onLink={() => navigate('/album')} />;
      return <div>{list.map(tr => renderTrade(tr, 'outgoing'))}</div>;
    }
    if (activeTab === 'Historial') {
      if (historialItems.length === 0) return <EmptyState icon="history" title={t('Aún no hiciste intercambios.')} linkText={t('Ir al álbum')} onLink={() => navigate('/album')} />;
      return (
        <div>
          {historialItems.map(item => {
            if (item._kind === 'offer') {
              // Render with a prefixed key to avoid collision with trade ids
              const node = renderOffer(item, { isMine: true });
              return React.cloneElement(node, { key: `offer-${item.id}` });
            }
            const node = renderHistory(item);
            return React.cloneElement(node, { key: `trade-${item.id}` });
          })}
        </div>
      );
    }
    if (activeTab === 'Mis ofertas') {
      const list = openOffers;
      return (
        <div>
          {onCreateOffer && (
            <div className="px-3 pt-3 pb-1">
              <button
                type="button"
                onClick={onCreateOffer}
                className="w-full flex items-center justify-center gap-2 px-4 py-2 rounded-xl text-sm font-bold text-white transition-all hover:opacity-90 active:scale-95"
                style={{ backgroundColor: 'var(--md-sys-color-primary, #2563eb)' }}
              >
                <span className="material-symbols-outlined text-base">add</span>
                {t('Nueva oferta')}
              </button>
            </div>
          )}
          {list.length === 0
            ? <EmptyState icon="local_offer" title={t('No publicaste ofertas todavía')} linkText={null} onLink={null} />
            : <div>{list.map(o => renderOffer(o, { isMine: true }))}</div>
          }
        </div>
      );
    }
    return null;
  }

  return (
    <div className="h-full flex flex-col" style={{ backgroundColor: 'var(--md-sys-color-surface-container, #f3f4f6)' }}>
      <main className="flex-1 overflow-y-auto hide-scrollbar">
        <div className="px-4 py-3">
          {toast && (
            <div className={`fixed top-4 left-1/2 -translate-x-1/2 z-50 px-4 py-2.5 rounded-xl shadow-lg text-sm font-bold flex items-center gap-2 transition-all ${toast.type === 'error' ? 'bg-red-500 text-white' : 'bg-surface-container text-on-surface border border-outline'}`}>
              <span className="material-symbols-outlined text-sm">{toast.type === 'error' ? 'error' : 'check_circle'}</span>
              {toast.message}
            </div>
          )}

          {/* ── Metrics ── */}
          <div className="grid grid-cols-4 gap-2 mb-3">
            <MiniMetric bucket="activas"     label={t('Activas')}     value={metrics.activas}     trades={tradesHook.activeTrades}    onPick={openTrade} />
            <MiniMetric bucket="recibidas"   label={t('Recibidas')}   value={metrics.recibidas}   trades={tradesHook.incomingTrades}  onPick={openTrade} />
            <MiniMetric bucket="enviadas"    label={t('Enviadas')}    value={metrics.enviadas}    trades={tradesHook.outgoingTrades}  onPick={openTrade} />
            <MiniMetric bucket="completadas" label={t('Completadas')} value={metrics.completadas} trades={tradesHook.historyTrades}   onPick={openTrade} />
          </div>

          {/* ── Tabs ── */}
          <div
            className="mb-3 rounded-xl p-1 overflow-x-auto border border-outline-variant/30"
            style={{ backgroundColor: 'var(--md-sys-color-surface, #ffffff)' }}
          >
            <div className="flex gap-0.5">
              {TABS.map(tab => (
                <button
                  key={tab}
                  type="button"
                  onClick={() => setActiveTab(tab)}
                  className={`shrink-0 px-2.5 py-1.5 rounded-lg text-center text-xs font-bold transition-colors whitespace-nowrap ${
                    activeTab === tab
                      ? 'bg-primary/10 text-primary'
                      : 'text-on-surface-variant hover:bg-surface-container'
                  }`}
                >
                  {t(tab)}
                </button>
              ))}
            </div>
          </div>

          {/* ── List ── */}
          <div className="bg-surface rounded-xl overflow-hidden shadow-md ring-1 ring-outline-variant">
            {TabBody()}
          </div>
        </div>
      </main>
    </div>
  );
}

export default Album2Dashboard;
